// Journal files (docs/plans/phase3-accounting-and-revenue-share.md §5, ADR 0034).
//
// Amounts are written from cents as plain decimals (`12500.00`), never through a float.
// Text is formula-safe. Nothing here names a patient: counterparties are payers and suppliers.

import { CSV_LINE_BREAK, toCsvRecordCells, type CsvCell } from '../csv/write.ts';
import { csvMoney, reportCsv } from '../reports/table-csv.ts';
import type { CalendarDay } from '../time.ts';
import { formatDay, formatIsoDay } from '../time.ts';
import type { JournalEventKind } from './journal.ts';
import { JOURNAL_EVENT_WORDS } from './journal.ts';
import type { JournalEntry } from './posting.ts';

export const JOURNAL_EXPORT_FORMATS = ['csv', 'detail', 'quickbooks'] as const;
export type JournalExportFormat = (typeof JOURNAL_EXPORT_FORMATS)[number];

export function entryDescription(entry: {
  kind: JournalEventKind;
  postedOn: CalendarDay;
  late: boolean;
}): string {
  const words = JOURNAL_EVENT_WORDS[entry.kind];
  return entry.late
    ? `Late changes to closed periods: ${words.toLowerCase()}`
    : `${words}, ${formatDay(entry.postedOn)}`;
}

/** Up to 21 characters, QuickBooks Online's journal number limit (TODO(verify-spec)). */
export function journalNumber(postedOn: CalendarDay, entryNo: number): string {
  return `WJ-${formatIsoDay(postedOn).replaceAll('-', '')}-${String(entryNo).padStart(4, '0')}`;
}

export const GENERIC_JOURNAL_HEADER = [
  'Entry',
  'Journal number',
  'Date',
  'Kind',
  'Late',
  'Account key',
  'Account code',
  'Account name',
  'Counterparty',
  'Debit',
  'Credit',
  'Description',
  'Facility',
] as const;

/** The summary journal: one row per entry line. */
export function genericJournalCsv(entries: readonly JournalEntry[], facilityCode: string): string {
  const rows: CsvCell[][] = entries.flatMap((e) =>
    e.lines.map((l) => [
      { number: String(e.entryNo) },
      journalNumber(e.postedOn, e.entryNo),
      formatIsoDay(e.postedOn),
      e.kind,
      e.late ? 'yes' : 'no',
      l.account,
      l.accountCode ?? '',
      l.accountName,
      l.counterparty ?? '',
      csvMoney(l.debitCents),
      csvMoney(l.creditCents),
      entryDescription(e),
      facilityCode,
    ]),
  );
  return reportCsv(GENERIC_JOURNAL_HEADER, rows);
}

export interface DetailRow {
  readonly postedOn: CalendarDay;
  readonly eventDay: CalendarDay;
  readonly kind: JournalEventKind;
  readonly late: boolean;
  readonly sourceType: string;
  readonly reference: string | null;
  readonly account: string;
  readonly accountCode: string | null;
  readonly accountName: string;
  readonly counterparty: string | null;
  readonly debitCents: bigint;
  readonly creditCents: bigint;
}

export const DETAIL_HEADER = [
  'Posted on',
  'Event date',
  'Kind',
  'Late',
  'Source',
  'Reference',
  'Account key',
  'Account code',
  'Account name',
  'Counterparty',
  'Debit',
  'Credit',
  'Facility',
] as const;

/** One row per source event and account, with its document number, for audit trails. */
export function detailCsv(rows: readonly DetailRow[], facilityCode: string): string {
  return reportCsv(
    DETAIL_HEADER,
    rows.map((r) => [
      formatIsoDay(r.postedOn),
      formatIsoDay(r.eventDay),
      r.kind,
      r.late ? 'yes' : 'no',
      r.sourceType,
      r.reference ?? '',
      r.account,
      r.accountCode ?? '',
      r.accountName,
      r.counterparty ?? '',
      csvMoney(r.debitCents),
      csvMoney(r.creditCents),
      facilityCode,
    ]),
  );
}

/**
 * QuickBooks Online "Import journal entries" columns, as Intuit's help describes them:
 * journal number, journal date, account, debits, credits, description, name, location.
 * TODO(verify-spec): check the header names and dd/mm/yyyy against the sample file of a
 * Kenyan QuickBooks Online company (plan §5, O1).
 */
export const QUICKBOOKS_HEADER = [
  'Journal No',
  'Journal Date',
  'Account',
  'Debits',
  'Credits',
  'Description',
  'Name',
  'Location',
] as const;

function quickbooksDate(day: CalendarDay): string {
  return `${String(day.day).padStart(2, '0')}/${String(day.month).padStart(2, '0')}/${String(day.year)}`;
}

/** The account as QuickBooks shows it when account numbers are on: `4000 Revenue`. */
function quickbooksAccount(code: string | null, name: string): string {
  return code === null || code.trim() === '' ? name : `${code} ${name}`;
}

/**
 * One QuickBooks journal per Wodi entry. A zero column is left blank, as in Intuit's sample.
 * No byte-order mark: QuickBooks' importer reads UTF-8 without one.
 */
export function quickbooksJournalCsv(
  entries: readonly JournalEntry[],
  facilityCode: string,
): string {
  const records = [
    toCsvRecordCells([...QUICKBOOKS_HEADER]),
    ...entries.flatMap((e) =>
      e.lines.map((l) =>
        toCsvRecordCells([
          journalNumber(e.postedOn, e.entryNo),
          quickbooksDate(e.postedOn),
          quickbooksAccount(l.accountCode, l.accountName),
          l.debitCents === 0n ? '' : csvMoney(l.debitCents),
          l.creditCents === 0n ? '' : csvMoney(l.creditCents),
          entryDescription(e),
          l.counterparty ?? '',
          facilityCode,
        ]),
      ),
    ),
  ];
  return records.map((r) => r + CSV_LINE_BREAK).join('');
}

/** `wodi-journal-<facility>-<from>-<to>.csv`: no patient and no filter value in the name. */
export function journalFileName(
  format: JournalExportFormat,
  facilityCode: string,
  from: CalendarDay,
  to: CalendarDay,
): string {
  const safe = facilityCode.replace(/[^A-Za-z0-9-]/g, '');
  const kind =
    format === 'quickbooks'
      ? 'journal-quickbooks'
      : format === 'detail'
        ? 'journal-detail'
        : 'journal';
  return `wodi-${kind}-${safe}-${formatIsoDay(from)}-${formatIsoDay(to)}.csv`;
}
